import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { T } from '../utils/theme';
import { fmtBRL, startOfDayCuiaba, endOfDayCuiaba, startOfWeekCuiaba, endOfWeekCuiaba, toCuiabaTimestamp, formatDataCuiaba, formatHoraCuiaba } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { LargeTitle, SolidCard, SectionHeader, Avatar, KpiTile, Segmented, Sparkline } from '../components/ui';
import { Users, DollarSign, XCircle, Bell, ArrowUp, ArrowDown, Calendar, Wallet, Clock, AlertCircle } from 'lucide-react';
import financeiroService from '../services/financeiroService';

const TZ_CUIABA = 'America/Cuiaba';

const PAY_PALETTE = ['#0A84FF', '#B0741F', '#BF5AF2', '#FF375F', '#5AC8FA'];

const classificarPagamento = (fp) => {
  if (!fp) return 'Outros';
  const v = fp.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (v.includes('pix')) return 'Pix';
  if (v.includes('dinheiro') || v.includes('especie')) return 'Dinheiro';
  if (v.includes('credito') || v.includes('credit')) return 'Credito';
  if (v.includes('debito') || v.includes('debit')) return 'Debito';
  return 'Outros';
};

const Dashboard = () => {
  const { user } = useAuth();
  const { comandas, agendamentos, barbeiros, configuracoes } = useData();
  const [despesasVencendo, setDespesasVencendo] = useState([]);
  const [periodoSelecionado, setPeriodoSelecionado] = useState('hoje');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  useEffect(() => {
    const carregarDespesasVencendo = async () => {
      try {
        const hoje = new Date();
        const amanha = new Date(hoje);
        amanha.setDate(hoje.getDate() + 1);
        const hojeStr = hoje.toISOString().split('T')[0];
        const amanhaStr = amanha.toISOString().split('T')[0];
        const despesas = await financeiroService.listarDespesas({ status: 'aberta', dataInicio: hojeStr, dataFim: amanhaStr });
        const despesasAbertas = despesas.filter(d => d.status === 'aberta');
        setDespesasVencendo([
          ...despesasAbertas.filter(d => d.data === hojeStr).map(d => ({ ...d, tipo: 'hoje' })),
          ...despesasAbertas.filter(d => d.data === amanhaStr).map(d => ({ ...d, tipo: 'amanha' })),
        ]);
      } catch (error) {
        if (error.isAuthError || error.response?.status === 401) return;
      }
    };
    if (user && (user.tipo === 'gestor' || user.tipo === 'caixa')) carregarDespesasVencendo();
  }, [user]);

  const periodoAtual = useMemo(() => {
    const hoje = new Date();
    let inicio = null, fim = null, label = '';
    switch (periodoSelecionado) {
      case 'hoje': inicio = startOfDayCuiaba(hoje); fim = endOfDayCuiaba(hoje); label = 'Hoje'; break;
      case 'ontem': {
        const ontem = new Date(hoje); ontem.setDate(hoje.getDate() - 1);
        inicio = startOfDayCuiaba(ontem); fim = endOfDayCuiaba(ontem); label = 'Ontem'; break;
      }
      case 'semana': inicio = startOfWeekCuiaba(hoje); fim = endOfWeekCuiaba(hoje);
        label = `Semana (${formatDataCuiaba(inicio)} - ${formatDataCuiaba(fim)})`; break;
      case 'mes': {
        const f = new Intl.DateTimeFormat('en-CA', { timeZone: TZ_CUIABA, year: 'numeric', month: '2-digit', day: '2-digit' });
        const [y, m] = f.format(hoje).split('-').map(Number);
        inicio = new Date(Date.UTC(y, m - 1, 1)); fim = new Date(Date.UTC(y, m, 0, 23, 59, 59, 999));
        label = `Este mes`; break;
      }
      case 'personalizado':
        if (!dataInicio || !dataFim) { label = 'Personalizado'; break; }
        inicio = startOfDayCuiaba(dataInicio); fim = endOfDayCuiaba(dataFim);
        label = `${formatDataCuiaba(inicio)} - ${formatDataCuiaba(fim)}`; break;
      default: inicio = startOfDayCuiaba(hoje); fim = endOfDayCuiaba(hoje); label = 'Hoje';
    }
    return { inicio, fim, label };
  }, [periodoSelecionado, dataInicio, dataFim]);

  const filtrarPorPeriodo = useCallback(() => {
    if (!periodoAtual.inicio || !periodoAtual.fim) return comandas;
    const inicioMs = periodoAtual.inicio.getTime(), fimMs = periodoAtual.fim.getTime();
    return comandas.filter(c => { const ms = toCuiabaTimestamp(c.data); return ms >= inicioMs && ms <= fimMs; });
  }, [comandas, periodoAtual]);

  const comandasFiltradas = user?.tipo === 'barbeiro'
    ? filtrarPorPeriodo().filter(c => c.barbeiroId === user.barbeiroId)
    : filtrarPorPeriodo();

  const agendamentosFiltrados = user?.tipo === 'barbeiro'
    ? agendamentos.filter(a => a.barbeiroId === user.barbeiroId) : agendamentos;

  const comandasPagas = comandasFiltradas.filter(c => c.status === 'pago');
  const faturamentoTotal = comandasPagas.reduce((s, c) => s + c.total, 0);
  const atendimentosTotal = comandasPagas.length;
  const ticketMedio = atendimentosTotal > 0 ? faturamentoTotal / atendimentosTotal : 0;
  const cancelados = comandasFiltradas.filter(c => c.status === 'cancelado').length;

  const comissaoTotal = useMemo(() => {
    return comandasPagas.reduce((total, c) => {
      if (!c.servicos || !Array.isArray(c.servicos)) return total;
      return total + c.servicos.reduce((sum, s) => {
        const pct = s.comissao || configuracoes.comissaoPadrao || 48;
        return sum + (parseFloat(s.preco || 0) * pct / 100);
      }, 0);
    }, 0);
  }, [comandasPagas, configuracoes]);

  // Period-over-period delta
  const deltaPercent = useMemo(() => {
    const hoje = new Date();
    let prevInicio, prevFim;
    if (periodoSelecionado === 'hoje') {
      const ontem = new Date(hoje); ontem.setDate(hoje.getDate() - 1);
      prevInicio = startOfDayCuiaba(ontem); prevFim = endOfDayCuiaba(ontem);
    } else if (periodoSelecionado === 'semana') {
      const mon = startOfWeekCuiaba(hoje);
      const prevMon = new Date(mon); prevMon.setUTCDate(prevMon.getUTCDate() - 7);
      const prevSat = new Date(prevMon); prevSat.setUTCDate(prevMon.getUTCDate() + 5);
      prevInicio = prevMon; prevFim = endOfDayCuiaba(prevSat);
    } else if (periodoSelecionado === 'mes') {
      const f = new Intl.DateTimeFormat('en-CA', { timeZone: TZ_CUIABA, year: 'numeric', month: '2-digit', day: '2-digit' });
      const [y, m] = f.format(hoje).split('-').map(Number);
      const pm = m - 1 === 0 ? 12 : m - 1;
      const py = m - 1 === 0 ? y - 1 : y;
      prevInicio = new Date(Date.UTC(py, pm - 1, 1));
      prevFim = new Date(Date.UTC(py, pm, 0, 23, 59, 59, 999));
    } else return null;

    if (!prevInicio || !prevFim) return null;
    const prevMs = prevInicio.getTime(), prevFimMs = prevFim.getTime();
    const prevComandas = (user?.tipo === 'barbeiro'
      ? comandas.filter(c => c.barbeiroId === user.barbeiroId)
      : comandas
    ).filter(c => {
      if (c.status !== 'pago') return false;
      const ms = toCuiabaTimestamp(c.data);
      return ms >= prevMs && ms <= prevFimMs;
    });
    const prevFat = prevComandas.reduce((s, c) => s + c.total, 0);
    if (prevFat === 0) return faturamentoTotal > 0 ? 100 : 0;
    return ((faturamentoTotal - prevFat) / prevFat * 100);
  }, [periodoSelecionado, comandas, faturamentoTotal, user]);

  // Comandas em aberto
  const comandasAbertas = comandas.filter(c => c.status === 'aberto' || c.status === 'pendente');
  const totalAbertas = comandasAbertas.reduce((s, c) => s + c.total, 0);

  // Últimos 7 dias sparkline
  const ultimos7Dias = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i); d.setHours(0, 0, 0, 0);
    const cDia = comandasPagas.filter(c => { const dc = new Date(c.data); dc.setHours(0, 0, 0, 0); return dc.getTime() === d.getTime(); });
    ultimos7Dias.push({ dia: d.toLocaleDateString('pt-BR', { weekday: 'short' }).slice(0, 3), valor: cDia.reduce((s, c) => s + c.total, 0) });
  }
  const sparkData = ultimos7Dias.map(d => d.valor);

  // Ranking de barbeiros (semana)
  const inicioSemanaMs = startOfWeekCuiaba(new Date()).getTime();
  const fimSemanaMs = endOfWeekCuiaba(new Date()).getTime();
  const comandasSemanaRanking = comandas.filter(c => {
    if (c.status !== 'pago') return false;
    const ms = toCuiabaTimestamp(c.data);
    return ms >= inicioSemanaMs && ms <= fimSemanaMs;
  });

  const rankingBarbeiros = barbeiros.filter(b => b.ativo).map(b => {
    const cb = comandasSemanaRanking.filter(c => c.barbeiroId === b.id);
    return { nome: b.nome, atendimentos: cb.length, faturamento: cb.reduce((s, c) => s + c.total, 0), barbeiroId: b.id };
  }).sort((a, b) => b.faturamento - a.faturamento || a.nome.localeCompare(b.nome));

  // Serviços mais vendidos
  const servicosVendidos = {};
  comandasPagas.forEach(c => { (c.servicos || []).forEach(s => { servicosVendidos[s.nome] = (servicosVendidos[s.nome] || 0) + 1; }); });
  const dadosServicos = Object.entries(servicosVendidos).map(([nome, quantidade]) => ({ nome, quantidade })).sort((a, b) => b.quantidade - a.quantidade).slice(0, 5);
  const maxServico = dadosServicos.length > 0 ? dadosServicos[0].quantidade : 1;

  // Formas de pagamento
  const pagamentoBreakdown = useMemo(() => {
    const map = {};
    comandasPagas.forEach(c => {
      const cat = classificarPagamento(c.formaPagamento);
      map[cat] = (map[cat] || 0) + c.total;
    });
    const entries = Object.entries(map).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
    const cats = ['Pix', 'Dinheiro', 'Credito', 'Debito', 'Outros'];
    return entries.map(([cat, valor]) => ({
      cat, valor, pct: (valor / total * 100).toFixed(1),
      color: PAY_PALETTE[cats.indexOf(cat)] || PAY_PALETTE[4],
    }));
  }, [comandasPagas]);

  // Próximos agendamentos
  const proximosAgendamentos = useMemo(() => {
    const agora = new Date();
    return agendamentosFiltrados
      .filter(a => a.status === 'confirmado' && new Date(a.data || a.dataHora) >= agora)
      .sort((a, b) => new Date(a.data || a.dataHora) - new Date(b.data || b.dataHora))
      .slice(0, 4);
  }, [agendamentosFiltrados]);

  const rankColors = [T.warning, T.label3, '#CD7F32', T.fill2, T.fill2];
  const barberColors = [T.warning, T.indigo, T.pink, T.success, T.purple];

  const hoje = new Date();
  const diaSemana = hoje.toLocaleDateString('pt-BR', { weekday: 'long' });
  const diaFormatado = hoje.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });

  const deltaLabel = periodoSelecionado === 'hoje' ? 'vs ontem'
    : periodoSelecionado === 'semana' ? 'vs semana anterior'
    : periodoSelecionado === 'mes' ? 'vs mes anterior' : '';

  return (
    <div style={{ padding: '0 0 40px' }}>
      <LargeTitle
        title="Inicio"
        subtitle={`Ola, ${user?.nome} — ${diaSemana}, ${diaFormatado}`}
        trailing={
          <button style={{
            border: 'none', cursor: 'pointer', width: 38, height: 38, borderRadius: 19,
            background: T.fill, color: T.label,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><Bell size={20} /></button>
        }
      />

      {/* Despesas vencendo */}
      {despesasVencendo.length > 0 && (
        <div style={{ padding: '0 20px 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {despesasVencendo.filter(d => d.tipo === 'hoje').length > 0 && (
            <div style={{ background: 'rgba(255,59,48,0.1)', borderRadius: 16, padding: '12px 16px', border: '1px solid rgba(255,59,48,0.2)' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.danger, marginBottom: 4 }}>
                {despesasVencendo.filter(d => d.tipo === 'hoje').length} conta(s) vencendo hoje
              </div>
              {despesasVencendo.filter(d => d.tipo === 'hoje').slice(0, 2).map(d => (
                <div key={d.id} style={{ fontSize: 12, color: T.danger, opacity: 0.8 }}>{d.titulo} — {fmtBRL(d.valor)}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Comandas em aberto alert */}
      {comandasAbertas.length > 0 && (
        <div style={{ padding: '0 20px 8px' }}>
          <div style={{
            background: 'rgba(255,159,10,0.1)', borderRadius: 16, padding: '12px 16px',
            border: '1px solid rgba(255,159,10,0.2)', display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <AlertCircle size={18} color={T.warning} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.warning }}>
                {comandasAbertas.length} comanda{comandasAbertas.length > 1 ? 's' : ''} em aberto
              </div>
              <div style={{ fontSize: 12, color: T.warning, opacity: 0.8 }}>Total pendente: {fmtBRL(totalAbertas)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Period filter */}
      <div style={{ padding: '6px 20px 0' }}>
        <Segmented
          items={[
            { value: 'hoje', label: 'Hoje' },
            { value: 'semana', label: 'Semana' },
            { value: 'mes', label: 'Mes' },
          ]}
          value={periodoSelecionado}
          onChange={setPeriodoSelecionado}
        />
      </div>

      {/* Hero KPI — Faturamento */}
      <div style={{ padding: '18px 20px 0' }}>
        <SolidCard padding={20} radius={24}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: T.label2, letterSpacing: '-0.01em' }}>
                Faturamento {periodoAtual.label.toLowerCase()}
              </div>
              <div className="tnum" style={{ fontSize: 36, fontWeight: 700, color: T.label, letterSpacing: '-0.03em', marginTop: 4 }}>
                {fmtBRL(faturamentoTotal)}
              </div>
            </div>
            {deltaPercent !== null && deltaPercent !== 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '5px 9px', borderRadius: 999,
                background: deltaPercent > 0 ? 'rgba(48,209,88,0.14)' : 'rgba(255,59,48,0.14)',
                color: deltaPercent > 0 ? T.success : T.danger,
                fontSize: 13, fontWeight: 600,
              }}>
                {deltaPercent > 0 ? <ArrowUp size={12} strokeWidth={2.4} /> : <ArrowDown size={12} strokeWidth={2.4} />}
                {Math.abs(deltaPercent).toFixed(0)}%
              </div>
            )}
          </div>
          {deltaLabel && (
            <div style={{ fontSize: 11, color: T.label3, marginTop: 2 }}>{deltaLabel}</div>
          )}
          {sparkData.some(v => v > 0) && (
            <div style={{ marginTop: 14, marginLeft: -4, marginRight: -4 }}>
              <Sparkline data={sparkData} color={T.accent} height={56} />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.label3, marginTop: 6, padding: '0 2px' }}>
            {ultimos7Dias.map((d, i) => <span key={i}>{d.dia}</span>)}
          </div>
        </SolidCard>
      </div>

      {/* KPI grid — 4 tiles, no more "Taxa ocupação" */}
      <div className="bx-kpi-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '12px 20px 0' }}>
        <KpiTile icon={Users} tint={T.accent} label="Atendimentos" value={atendimentosTotal} sub={periodoAtual.label.toLowerCase()} />
        <KpiTile icon={DollarSign} tint={T.success} label="Ticket medio" value={fmtBRL(ticketMedio)} sub="por cliente" />
        <KpiTile icon={Wallet} tint={T.purple} label="Comissoes" value={fmtBRL(comissaoTotal)} sub={periodoAtual.label.toLowerCase()} />
        <KpiTile icon={XCircle} tint={T.danger} label="Cancelados" value={cancelados} sub={periodoAtual.label.toLowerCase()} />
      </div>

      <div className="bx-cols-2">
        {/* Próximos agendamentos */}
        <div style={{ padding: '4px 20px 0' }}>
          <SectionHeader title="Proximos agendamentos" />
          <SolidCard padding={0} radius={20}>
            {proximosAgendamentos.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: T.label3, fontSize: 14 }}>Nenhum agendamento confirmado</div>
            ) : proximosAgendamentos.map((a, i) => {
              const dt = new Date(a.data || a.dataHora);
              const bNome = barbeiros.find(b => b.id === a.barbeiroId)?.nome || '';
              return (
                <div key={a.id || i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  borderBottom: i < proximosAgendamentos.length - 1 ? `0.5px solid ${T.separator}` : 'none',
                }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: 'rgba(10,132,255,0.1)', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Calendar size={16} color={T.accent} strokeWidth={2} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.label }}>{a.clienteNome || a.cliente || 'Cliente'}</div>
                    <div style={{ fontSize: 12, color: T.label2, marginTop: 1 }}>
                      {bNome ? `${bNome} · ` : ''}{formatHoraCuiaba(dt)} · {formatDataCuiaba(dt)}
                    </div>
                  </div>
                  <div style={{
                    fontSize: 11, fontWeight: 600, color: T.success, background: 'rgba(48,209,88,0.14)',
                    padding: '2px 8px', borderRadius: 999,
                  }}>Confirmado</div>
                </div>
              );
            })}
          </SolidCard>
        </div>

        {/* Formas de pagamento */}
        <div style={{ padding: '4px 20px 0' }}>
          <SectionHeader title="Formas de pagamento" />
          <SolidCard padding={16} radius={20}>
            {pagamentoBreakdown.length === 0 ? (
              <div style={{ textAlign: 'center', color: T.label3, fontSize: 14, padding: 8 }}>Sem dados</div>
            ) : (
              <>
                <div style={{
                  display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden',
                  background: T.fill, marginBottom: 16,
                }}>
                  {pagamentoBreakdown.map((p, i) => (
                    <div key={p.cat} style={{
                      width: `${p.pct}%`, background: p.color,
                      borderRadius: i === 0 ? '5px 0 0 5px' : i === pagamentoBreakdown.length - 1 ? '0 5px 5px 0' : 0,
                    }} />
                  ))}
                </div>
                {pagamentoBreakdown.map(p => (
                  <div key={p.cat} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0',
                  }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: p.color, flexShrink: 0 }} />
                    <div style={{ flex: 1, fontSize: 14, fontWeight: 500, color: T.label }}>{p.cat}</div>
                    <div className="tnum" style={{ fontSize: 14, fontWeight: 600, color: T.label }}>{fmtBRL(p.valor)}</div>
                    <div className="tnum" style={{ fontSize: 12, color: T.label3, width: 44, textAlign: 'right' }}>{p.pct}%</div>
                  </div>
                ))}
              </>
            )}
          </SolidCard>
        </div>
      </div>

      <div className="bx-cols-2">
        {/* Ranking de barbeiros */}
        <div style={{ padding: '4px 20px 0' }}>
          <SectionHeader title="Ranking de barbeiros" />
          <SolidCard padding={0} radius={20}>
            {rankingBarbeiros.slice(0, 5).map((b, i) => {
              const podeVer = user?.tipo !== 'barbeiro';
              const ehLogado = user?.tipo === 'barbeiro' && user?.barbeiroId === b.barbeiroId;
              const mostrar = podeVer || ehLogado;
              return (
                <div key={b.nome} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  borderBottom: i < Math.min(rankingBarbeiros.length, 5) - 1 ? `0.5px solid ${T.separator}` : 'none',
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 14,
                    background: i < 3 ? rankColors[i] : T.fill2,
                    color: i < 3 ? '#fff' : T.label2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 13, flexShrink: 0,
                  }}>{i + 1}</div>
                  <Avatar name={b.nome} size={36} tint={barberColors[i]} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: T.label, letterSpacing: '-0.01em' }}>{b.nome}</div>
                    <div style={{ fontSize: 12, color: T.label2, marginTop: 1 }}>{b.atendimentos} atendimentos</div>
                  </div>
                  <div className="tnum" style={{ fontSize: 15, fontWeight: 600, color: T.label, letterSpacing: '-0.01em' }}>
                    {mostrar ? fmtBRL(b.faturamento) : '***'}
                  </div>
                </div>
              );
            })}
            {rankingBarbeiros.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: T.label3, fontSize: 14 }}>Nenhum barbeiro com atendimentos</div>
            )}
          </SolidCard>
        </div>

        {/* Serviços mais vendidos — CSS bars instead of recharts */}
        {dadosServicos.length > 0 && (
          <div style={{ padding: '4px 20px 0' }}>
            <SectionHeader title="Servicos mais vendidos" />
            <SolidCard padding={16} radius={20}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {dadosServicos.map((s, i) => (
                  <div key={s.nome}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: T.label }}>{s.nome}</span>
                      <span className="tnum" style={{ fontSize: 14, fontWeight: 600, color: T.label }}>{s.quantidade}</span>
                    </div>
                    <div style={{ height: 7, borderRadius: 4, background: T.fill, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 4, width: `${(s.quantidade / maxServico * 100)}%`,
                        background: `linear-gradient(90deg, ${T.accent}, ${T.indigo})`,
                        transition: 'width 400ms ease',
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            </SolidCard>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
